import React from "react"
import { isEmpty, startCase } from "lodash"
import Button from "components/common/Button"
import Webcam from "react-webcam"
import Panel from "components/panel"
import Text from "components/text"
import cx from "classnames"
import FormDropDown from "components/form/dropdown"

import ImagePlaceholder from "assets/images/imageplaceholder.png"
import ApptitudeLogo from "assets/images/apptitude-logo.png"

import { isIdVerified } from "helpers/util"

import "./index.scss"

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: "user",
}

class AuthenticateForm extends React.Component {
  selectRefs = [React.createRef(), React.createRef()]

  constructor(props) {
    super(props)
    this.state = {
      webCamEnabled: false,
      canRetake: false,
    }
  }

  componentDidUpdate(prevProps) {
    const { errors: prevError } = prevProps
    const { errors } = this.props

    if (!isEmpty(errors) && prevError !== errors) {
      for (let fieldName in errors) {
        if (this.selectRefs[fieldName]) {
          let field = this.selectRefs[fieldName]

          if (field?.current) {
            field.current.focus()
          }
        } else {
          let field = document.getElementsByName(fieldName)
          if (field.length > 0) {
            field[0].focus()
          }
        }
        break
      }
    }
  }

  handleToggleWebcam = () => {
    const { webCamEnabled } = this.state

    this.setState({
      webCamEnabled: !webCamEnabled,
      canRetake: false,
    })
  }

  handleRetake = () => {
    const { handleRetake } = this.props

    this.setState({
      canRetake: false,
    })

    if (handleRetake) {
      handleRetake()
    }
  }

  handleScreenShot = () => {
    const { handleScreenShot } = this.props

    this.setState({
      canRetake: true,
    })

    if (handleScreenShot) {
      handleScreenShot()
    }
  }

  formatErrors = (errors) => {
    if (Array.isArray(errors)) {
      return `* ${errors.join("\n* ")}`
    } else {
      return `* ${errors}`
    }
  }

  renderId = (idx, excludeOptions = []) => {
    const {
      isLoadingOptions,
      options = [],
      handleChangeType,
      handleChangeImage,
      authForm,
      inputFileRef,
      handleImageClick,
      errors = {},
    } = this.props
    const { user_ids = [] } = authForm

    const idData = !isEmpty(user_ids && user_ids[idx]) ? user_ids[idx] : {}

    const status = idData.verified
      ? "verified"
      : idData.submitted
      ? "pending"
      : false

    return (
      <div className="upload-container">
        {idData.verified || idData.submitted ? (
          <>
            <h1 className="step-title">
              Uploaded ID: {idData?.template}
              {status && (
                <span className={`badge ${status}`}>{startCase(status)}</span>
              )}
            </h1>
            <div className="upload-id">
              <img src={idData?.photo_id} alt="uploaded-id" />
            </div>
          </>
        ) : (
          <>
            <FormDropDown
              label="ID Type"
              name="mainTemplateId"
              handleChange={(e) => handleChangeType(e, idx)}
              options={options.filter(
                (option) =>
                  !excludeOptions.includes(option.label) &&
                  !excludeOptions.includes(option.value)
              )}
              value={idData.template}
              required={true}
              error={errors[`id-template-${idx}`]}
              loading={isLoadingOptions}
              fieldRef={this.selectRefs[idx]}
            />
            {idData.template && (
              <>
                <div className="form-input -vertical -upload">
                  <Text type="label" text="Upload Your ID" />
                  <div
                    className="form-image"
                    onClick={(e) => handleImageClick(e, idx)}
                  >
                    {!isEmpty(user_ids) && user_ids[idx]?.photo_id ? (
                      <img
                        onError={(e) => (e.target.src = ImagePlaceholder)}
                        src={user_ids[idx].photo_id}
                        alt="uploaded-id"
                      />
                    ) : (
                      <div className="input-placeholder">
                        <img src={ApptitudeLogo} alt="apptitude-logo" />
                        <Button
                          label="Upload ID"
                          onClick={(e) => handleImageClick(e, idx)}
                          block
                        />
                      </div>
                    )}
                  </div>
                  <input
                    accept="image/png, image/jpeg"
                    type="file"
                    style={{ display: "none" }}
                    ref={inputFileRef[idx]}
                    onChange={(e) => handleChangeImage(e, idx)}
                  />
                  <div className="form-hint">
                    Please take a clear photo of your ID. Save to your
                    computer/phone. Click the box above to upload.
                  </div>
                  {errors[`id-image-${idx}`] && (
                    <p className="error-message">
                      {this.formatErrors(errors[`id-image-${idx}`])}
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    )
  }

  renderSelfie = (
    selfieEnabled = false,
    isVerified = false,
    manualKYCSubmitted = false
  ) => {
    const { forwardRef, authForm, errors = {} } = this.props
    const { webCamEnabled, canRetake } = this.state

    return (
      <div className="upload-container">
        {selfieEnabled ? (
          <div className="form-input -vertical -upload">
            <Text type="label" text="Take a Selfie" />
            {webCamEnabled ? (
              <div className="form-image">
                <Webcam
                  ref={forwardRef}
                  audio={false}
                  height={320}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  mirrored={true}
                />
              </div>
            ) : (
              <div className="form-image">
                <img
                  src={authForm.selfie_image || ImagePlaceholder}
                  alt="uploaded-selfie"
                  onError={(e) => (e.target.src = ImagePlaceholder)}
                />
              </div>
            )}
            <div className="form-hint">
              <Button
                label={webCamEnabled ? "Close Camera" : "Open Camera"}
                outlined={webCamEnabled}
                onClick={this.handleToggleWebcam}
              />
              {webCamEnabled && (
                <Button
                  label={canRetake ? "Re-take snapshot" : "Take a snapshot"}
                  onClick={() => {
                    canRetake ? this.handleRetake() : this.handleScreenShot()
                  }}
                />
              )}
            </div>
            {errors?.selfie_image && (
              <p className="error-message">
                {this.formatErrors(errors["selfie_image"])}
              </p>
            )}
          </div>
        ) : (
          (isVerified || manualKYCSubmitted) && (
            <>
              <h1 className="step-title mt-m">Uploaded Selfie</h1>
              <div className="upload-selfie">
                <img src={authForm.selfie_image} alt="Selfie" />
              </div>
            </>
          )
        )}
      </div>
    )
  }

  render() {
    const {
      loading,
      handleSubmitForVerification,
      authForm,
      isSecondIdRequired,
      schoolPrimaryId,
      kycError,
    } = this.props
    const { user_ids = [] } = authForm

    const isVerified = isIdVerified(user_ids, schoolPrimaryId)
    // const newSubmission = user_ids?.every((item) => item.new_id)

    const manualKYCSubmitted =
      isEmpty(user_ids) || (isSecondIdRequired && user_ids.length < 2)
        ? false
        : user_ids?.every((item) => item.submitted)

    const verifiedAndSubmitted = user_ids[0]?.verified && user_ids[1]?.submitted

    const canSubmitVerification =
      !isVerified && !manualKYCSubmitted && !verifiedAndSubmitted

    // const canManualSubmit = canSubmitVerification && !newSubmission ||
    const canManualSubmit = canSubmitVerification && !isEmpty(kycError)

    const mainTemplateId = !isEmpty(user_ids && user_ids[0])
      ? user_ids[0].template
      : null

    const secondTemplateId = !isEmpty(user_ids && user_ids[1])
      ? user_ids[1].template
      : null

    const canRetakeSelfie = canManualSubmit || canSubmitVerification

    return (
      <div className="authenticate-id">
        <Panel pad="24">
          <div className="note-container">
            <span className="icon-success">
              <i
                className={cx(
                  "fas fa-3x",
                  isVerified
                    ? "fa-check-circle"
                    : "fa-exclamation-circle warning"
                )}
              ></i>
            </span>

            <p>
              {isVerified
                ? "Your account is fully verified."
                : verifiedAndSubmitted
                ? "One of your IDs is still pending verification."
                : manualKYCSubmitted && isSecondIdRequired
                ? "Your IDs are still pending verification."
                : manualKYCSubmitted
                ? "Your ID is still pending verification."
                : `Verify your identity by uploading a valid ID and a selfie for
                        liveness check. Please take the photo indoors, in a well-lit area.
                        Try to approximate the photo in the ID submitted (e.g. please take
                        off your glasses if you aren't wearing any in the ID).`}
            </p>
          </div>
        </Panel>
        <Panel pad="40" grid>
          <Text type="headline" text="Authenticate Your ID" />
          <div className="upload-wrapper">
            {this.renderId(0, [secondTemplateId])}
            {mainTemplateId &&
              isSecondIdRequired &&
              this.renderId(1, [schoolPrimaryId, mainTemplateId])}
            {this.renderSelfie(canRetakeSelfie, isVerified, manualKYCSubmitted)}
          </div>
          <div className="actions -submit">
            {canManualSubmit && (
              <Button
                label="Submit for Manual Verification"
                size="large"
                outlined
                onClick={() => handleSubmitForVerification(true)}
                loading={loading}
              />
            )}
            {canSubmitVerification && (
              <Button
                label="Submit"
                size="large"
                onClick={() => handleSubmitForVerification(false)}
                loading={loading}
              />
            )}
          </div>
        </Panel>
      </div>
    )
  }
}

export default AuthenticateForm
